import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { useContacts } from "../hooks";
import styles from "./SearchableTradesSelect.module.css";
export const SearchableTradesSelect = ({ value, onChange, placeholder = "Search contacts...", disabled = false, allowClear = true, filter = "all", }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [internalSearchTerm, setInternalSearchTerm] = useState("");
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const isFocusedRef = useRef(false);
    const selectedContactNameRef = useRef("");
    const justSelectedRef = useRef(false);
    const searchTimeoutRef = useRef(null);
    // Fetch contacts independently for this dropdown
    // Use internal search term that's debounced
    const { contacts, loading: contactsLoading } = useContacts(filter, internalSearchTerm, value ? [value] : []);
    // Get selected contact for display
    const selectedContact = contacts.find((c) => c.id === value);
    // Filter contacts based on search term
    const filteredContacts = contacts.filter((contact) => (contact.name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
        (contact.email || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()));
    // Debounce search term for API calls
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        // Only update internal search term when user is actively typing
        if (isFocusedRef.current) {
            searchTimeoutRef.current = setTimeout(() => {
                setInternalSearchTerm(searchTerm);
            }, 300); // 300ms debounce
        }
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);
    // Handle input change
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setSearchTerm(newValue);
        setShowDropdown(true);
        // If input is cleared, clear selection only if allowed
        if (!newValue && allowClear) {
            onChange("");
            setInternalSearchTerm("");
            setShowDropdown(false);
            selectedContactNameRef.current = "";
        }
        else if (!newValue && !allowClear) {
            // Clearing the input text should be allowed so the user can type a new search term.
            // We only prevent clearing the *selection*; restoration happens on blur/click-outside.
            setInternalSearchTerm("");
        }
    };
    // Handle input focus
    const handleInputFocus = () => {
        isFocusedRef.current = true;
        setShowDropdown(true);
        inputRef.current?.select();
    };
    // Handle input blur
    const handleInputBlur = () => {
        isFocusedRef.current = false;
        // Use setTimeout to allow click events (like selection) to fire first
        // Then restore the selected contact's name if no selection was made
        setTimeout(() => {
            // Don't restore if a selection just happened
            if (justSelectedRef.current) {
                justSelectedRef.current = false;
                return;
            }
            // Close dropdown when leaving the input
            setShowDropdown(false);
            // If a selection exists, restore its display text; otherwise keep empty.
            if (value && selectedContactNameRef.current) {
                setSearchTerm(selectedContactNameRef.current);
            }
        }, 150);
    };
    // Handle contact selection
    const handleContactSelect = (contactId) => {
        const contact = contacts.find((c) => c.id === contactId);
        if (contact) {
            const contactName = contact.name || contact.email;
            // Store the selected contact's name for later restoration
            selectedContactNameRef.current = contactName;
            // Mark that a selection just happened to prevent blur from restoring
            justSelectedRef.current = true;
            onChange(contactId);
            setSearchTerm(contactName);
            setShowDropdown(false);
            isFocusedRef.current = false;
        }
    };
    // Handle clear button
    const handleClear = () => {
        setSearchTerm("");
        setInternalSearchTerm("");
        onChange("");
        selectedContactNameRef.current = "";
        setShowDropdown(false);
        isFocusedRef.current = false;
    };
    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            if (wrapperRef.current && !wrapperRef.current.contains(target)) {
                setShowDropdown(false);
                isFocusedRef.current = false;
                // Reset search term to stored contact name when clicking outside
                // Don't call onSearchChange - just restore the display text locally
                if (selectedContactNameRef.current) {
                    setSearchTerm(selectedContactNameRef.current);
                }
                else {
                    setSearchTerm("");
                }
            }
        };
        if (showDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [showDropdown]);
    // Update search term when value changes externally (only when not focused)
    // This prevents resets when user is typing in the input
    useEffect(() => {
        // Only sync if component is not actively being used (not focused)
        if (!isFocusedRef.current) {
            if (selectedContact) {
                const contactName = selectedContact.name || selectedContact.email;
                setSearchTerm(contactName);
                // Store the name for later restoration
                selectedContactNameRef.current = contactName;
            }
            else {
                setSearchTerm("");
                selectedContactNameRef.current = "";
            }
        }
    }, [value, selectedContact]);
    return (_jsxs("div", { className: styles.wrapper, ref: wrapperRef, children: [_jsxs("div", { className: styles.inputWrapper, children: [_jsx("input", { ref: inputRef, type: "text", className: styles.input, value: searchTerm, onChange: handleInputChange, onFocus: handleInputFocus, onBlur: handleInputBlur, placeholder: placeholder, disabled: disabled }), selectedContact && allowClear && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClear, disabled: disabled, children: "\u00D7" }))] }), showDropdown && !disabled && (_jsx("div", { className: styles.dropdown, children: contactsLoading ? (_jsx("div", { className: styles.empty, children: "Loading contacts..." })) : filteredContacts.length > 0 ? (filteredContacts.map((contact) => {
                    const displayText = contact.name || contact.email;
                    return (_jsx("button", { type: "button", className: styles.dropdownItem, onClick: () => handleContactSelect(contact.id), children: _jsx("span", { className: styles.itemText, children: displayText }) }, contact.id));
                })) : (_jsx("div", { className: styles.empty, children: "No contacts found" })) }))] }));
};
